import React, { useMemo } from 'react';
import { ITEMS, INVENTORY, ORDERS, VENDORS } from '../data/erpData';
import { useLang } from '../i18n/LangContext';
import '../styles/Dashboard.css';

const DashboardPage = ({ onNavigate }) => {
  const { t, fmtCurrency, fmtNum, lang } = useLang();
  const fmt = (n) => fmtNum(n);

  // 상태 텍스트 매핑
  const statusLabel = (s) => {
    if (s === '정상') return t.dashboard.statusNormal;
    if (s === '주의') return t.dashboard.statusWarning;
    return t.dashboard.statusOrder;
  };

  const stats = useMemo(() => {
    const needOrder = INVENTORY.filter(i => i.needOrder).length;
    const totalDefect = INVENTORY.reduce((s, i) => s + i.defect, 0);
    const totalLoss = INVENTORY.reduce((s, i) => s + i.lossAmt, 0);
    const totalGood = INVENTORY.reduce((s, i) => s + i.good, 0);
    const totalReserved = INVENTORY.reduce((s, i) => s + i.reserved, 0);
    const totalValue = INVENTORY.reduce((s, i) => s + i.good * i.cost, 0);
    const normalCount = INVENTORY.filter(i => i.status === '정상').length;
    const cautionCount = INVENTORY.filter(i => i.status === '주의').length;
    const orderShortage = ORDERS.filter(o => {
      const inv = INVENTORY.find(i => i.code === o.itemCode);
      return inv ? inv.atp < o.qty : true;
    }).length;
    return { needOrder, totalDefect, totalLoss, totalGood, totalReserved, totalValue, normalCount, cautionCount, orderShortage };
  }, []);

  const groupCounts = useMemo(() => {
    const map = {};
    ITEMS.forEach(it => { map[it.group] = (map[it.group] || 0) + 1; });
    return map;
  }, []);

  const topShortage = useMemo(() =>
    INVENTORY.filter(i => i.atp < 0).sort((a, b) => a.atp - b.atp).slice(0, 6), []);

  const topDefects = useMemo(() =>
    INVENTORY.filter(i => i.defect > 0).sort((a, b) => b.lossAmt - a.lossAmt).slice(0, 6), []);

  const vendorPerf = useMemo(() => {
    const map = {};
    VENDORS.forEach(v => {
      if (!map[v.vName]) map[v.vName] = { count: 0, totalLt: 0, totalDefRate: 0, totalCost: 0 };
      map[v.vName].count++;
      map[v.vName].totalLt += v.lt;
      map[v.vName].totalDefRate += v.defRate;
      map[v.vName].totalCost += v.cost;
    });
    return Object.entries(map).map(([name, d]) => ({
      name, count: d.count,
      avgLt: (d.totalLt / d.count).toFixed(1),
      avgDefRate: ((d.totalDefRate / d.count) * 100).toFixed(1),
      avgCost: Math.round(d.totalCost / d.count),
    }));
  }, []);

  const statusDist = useMemo(() => {
    const normal = INVENTORY.filter(i => i.status === '정상').length;
    const caution = INVENTORY.filter(i => i.status === '주의').length;
    const critical = INVENTORY.filter(i => i.status === '발주필요').length;
    const total = INVENTORY.length;
    return { normal, caution, critical, total };
  }, []);

  const orderAnalysis = useMemo(() => {
    return ORDERS.map(o => {
      const item = ITEMS.find(it => it.code === o.itemCode);
      const inv = INVENTORY.find(it => it.code === o.itemCode);
      const atp = inv ? inv.atp : 0;
      const shortage = Math.max(0, o.qty - atp);
      return { ...o, itemName: item?.name || '-', atp, shortage, fulfilled: atp >= o.qty };
    });
  }, []);

  // 그룹명 번역 (데이터 내 한국어 그룹명)
  const groupLabel = (g) => {
    if (lang === 'ja') {
      if (g === '메모리') return 'メモリ';
      if (g === '비메모리') return '非メモリ';
      if (g === '수동소자') return '受動素子';
      return g;
    }
    return g;
  };

  const kpiCards = [
    { label: t.dashboard.totalItems, value: `${ITEMS.length}${t.dashboard.pcs}`, icon: '📦', color: '#3b82f6', bg: '#eff6ff' },
    { label: `Good ${lang === 'ko' ? '재고' : '在庫'}`, value: `${fmt(stats.totalGood)} EA`, icon: '✅', color: '#10b981', bg: '#ecfdf5' },
    { label: t.dashboard.needOrder, value: `${stats.needOrder}${t.dashboard.count}`, icon: '⚠️', color: '#f59e0b', bg: '#fffbeb' },
    { label: `${lang === 'ko' ? '불량 재고' : '不良在庫'}`, value: `${fmt(stats.totalDefect)} EA`, icon: '🔴', color: '#ef4444', bg: '#fef2f2' },
    { label: `${lang === 'ko' ? '재고 자산가치' : '在庫資産価値'}`, value: fmtCurrency(stats.totalValue), icon: '💰', color: '#8b5cf6', bg: '#f5f3ff' },
    { label: t.dashboard.totalLoss, value: fmtCurrency(stats.totalLoss), icon: '📉', color: '#dc2626', bg: '#fef2f2' },
  ];

  const alertText1 = lang === 'ko'
    ? <>가용재고 부족 품목 <strong>{stats.needOrder}{t.dashboard.count}</strong> — 긴급 발주 검토가 필요합니다</>
    : <>可用在庫不足品目 <strong>{stats.needOrder}{t.dashboard.count}</strong> — 緊急発注の検討が必要です</>;
  const alertText2 = lang === 'ko'
    ? <>고객 주문 미충족 <strong>{stats.orderShortage}{t.dashboard.count}</strong> — 주문 대비 재고가 부족합니다</>
    : <>顧客受注未充足 <strong>{stats.orderShortage}{t.dashboard.count}</strong> — 受注に対し在庫が不足しています</>;

  return (
    <div className="dashboard">
      {/* KPI Cards */}
      <div className="kpi-grid">
        {kpiCards.map(card => (
          <div key={card.label} className="kpi-card" style={{ borderLeftColor: card.color }}>
            <div className="kpi-header">
              <span className="kpi-icon" style={{ background: card.bg }}>{card.icon}</span>
              <span className="kpi-label">{card.label}</span>
            </div>
            <div className="kpi-value" style={{ color: card.color }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* 긴급 알림 배너 */}
      {(stats.needOrder > 0 || stats.orderShortage > 0) && (
        <div className="alert-banner">
          <div className="alert-banner-content">
            <span className="alert-icon">🚨</span>
            <div className="alert-texts">
              {stats.needOrder > 0 && <span className="alert-text">{alertText1}</span>}
              {stats.orderShortage > 0 && <span className="alert-text">{alertText2}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Row 1: 재고상태 + 부족TOP + 불량TOP */}
      <div className="dashboard-row-3">
        <div className="dash-card">
          <h3 className="dash-card-title">📊 {t.dashboard.invStatus}</h3>
          <div className="status-gauge">
            <div className="gauge-bar">
              <div className="gauge-fill gauge-normal" style={{ width: `${(statusDist.normal / statusDist.total) * 100}%` }} />
              <div className="gauge-fill gauge-caution" style={{ width: `${(statusDist.caution / statusDist.total) * 100}%` }} />
              <div className="gauge-fill gauge-critical" style={{ width: `${(statusDist.critical / statusDist.total) * 100}%` }} />
            </div>
            <div className="gauge-legend">
              <div className="gauge-legend-item">
                <span className="legend-dot" style={{ background: '#10b981' }}></span>
                <span>{t.dashboard.statusNormal} {statusDist.normal}{t.dashboard.pcs}</span>
                <span className="legend-pct">{Math.round(statusDist.normal / statusDist.total * 100)}%</span>
              </div>
              <div className="gauge-legend-item">
                <span className="legend-dot" style={{ background: '#f59e0b' }}></span>
                <span>{t.dashboard.statusWarning} {statusDist.caution}{t.dashboard.pcs}</span>
                <span className="legend-pct">{Math.round(statusDist.caution / statusDist.total * 100)}%</span>
              </div>
              <div className="gauge-legend-item">
                <span className="legend-dot" style={{ background: '#ef4444' }}></span>
                <span>{t.dashboard.statusOrder} {statusDist.critical}{t.dashboard.pcs}</span>
                <span className="legend-pct">{Math.round(statusDist.critical / statusDist.total * 100)}%</span>
              </div>
            </div>
          </div>

          <h3 className="dash-card-title" style={{ marginTop: 24 }}>{lang === 'ko' ? '품목그룹별 분포' : '品目グループ別分布'}</h3>
          <div className="group-bars">
            {Object.entries(groupCounts).map(([group, count]) => {
              const pct = Math.round(count / ITEMS.length * 100);
              const color = group === '메모리' ? '#3b82f6' : group === '비메모리' ? '#8b5cf6' : '#06b6d4';
              return (
                <div key={group} className="group-bar-item">
                  <div className="group-bar-label">
                    <span>{groupLabel(group)}</span>
                    <span className="group-bar-count">{count}{t.dashboard.pcs} ({pct}%)</span>
                  </div>
                  <div className="group-bar-track">
                    <div className="group-bar-fill" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dash-card">
          <h3 className="dash-card-title">🚨 {lang === 'ko' ? '재고 부족 TOP' : '在庫不足 TOP'}</h3>
          {topShortage.length === 0 && <p className="dash-empty">{lang === 'ko' ? '부족 품목 없음' : '不足品目なし'}</p>}
          <div className="dash-list">
            {topShortage.map((item, i) => (
              <div key={item.code + i} className="dash-list-item">
                <div className="dash-list-info">
                  <span className="dash-list-code">{item.code}</span>
                  <span className="dash-list-name">{item.name.split(' ; ')[1] || item.name}</span>
                </div>
                <span className="dash-list-badge badge-red">{fmt(item.atp)} EA</span>
              </div>
            ))}
          </div>
        </div>

        <div className="dash-card">
          <h3 className="dash-card-title">📉 {lang === 'ko' ? '불량 손실 TOP' : '不良損失 TOP'}</h3>
          <div className="dash-list">
            {topDefects.map((item, i) => (
              <div key={item.code + i} className="dash-list-item">
                <div className="dash-list-info">
                  <span className="dash-list-code">{item.code}</span>
                  <span className="dash-list-name">{item.defect}{t.dashboard.pcs} × {fmtCurrency(item.cost)}</span>
                </div>
                <span className="dash-list-loss">{fmtCurrency(item.lossAmt)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: 주문 현황 */}
      <div className="dashboard-row-2" style={{ marginTop: 16 }}>
        <div className="dash-card">
          <h3 className="dash-card-title">🛒 {t.dashboard.orderFulfill}</h3>
          <table className="dash-table">
            <thead>
              <tr>
                <th>{t.orders.orderId}</th>
                <th>{t.orders.customer}</th>
                <th>{t.orders.itemCode}</th>
                <th>{t.orders.qty}</th>
                <th>ATP</th>
                <th>{t.orders.shortage}</th>
                <th>{t.inventory.status}</th>
                <th>{lang === 'ko' ? '구매규칙' : '購買ルール'}</th>
              </tr>
            </thead>
            <tbody>
              {orderAnalysis.map(order => (
                <tr key={order.id}>
                  <td><strong>{order.id}</strong></td>
                  <td>{order.customer}</td>
                  <td>{order.itemCode}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(order.qty)}</td>
                  <td style={{ textAlign: 'right', color: order.atp < order.qty ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                    {fmt(order.atp)}
                  </td>
                  <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: order.shortage > 0 ? 700 : 400 }}>
                    {order.shortage > 0 ? fmt(order.shortage) : '-'}
                  </td>
                  <td>
                    <span className={`table-badge ${order.fulfilled ? 'badge-green' : 'badge-red'}`}>
                      {order.fulfilled ? `✅ ${t.orders.fulfilled}` : `❌ ${t.orders.shortage}`}
                    </span>
                  </td>
                  <td>
                    <span className={`table-badge ${order.rule === 'Cost' ? 'badge-blue' : 'badge-green'}`}>
                      {order.rule}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Row 3: 공급업체 성과 */}
      <div className="dashboard-row-2" style={{ marginTop: 16 }}>
        <div className="dash-card">
          <h3 className="dash-card-title">🤝 {t.dashboard.vendorPerf}</h3>
          <table className="dash-table">
            <thead>
              <tr>
                <th>{t.vendors.vendorName}</th>
                <th>{lang === 'ko' ? '거래 품목 수' : '取引品目数'}</th>
                <th>{lang === 'ko' ? '평균 리드타임' : '平均リードタイム'}</th>
                <th>{lang === 'ko' ? '평균 불량율' : '平均不良率'}</th>
                <th>{lang === 'ko' ? '평균 단가' : '平均単価'}</th>
                <th>{lang === 'ko' ? '성과 등급' : '成果等級'}</th>
              </tr>
            </thead>
            <tbody>
              {vendorPerf.map(v => {
                const grade = parseFloat(v.avgDefRate) < 1.0 ? 'A' : parseFloat(v.avgDefRate) < 2.0 ? 'B' : 'C';
                const gradeColor = grade === 'A' ? 'badge-green' : grade === 'B' ? 'badge-blue' : 'badge-red';
                return (
                  <tr key={v.name}>
                    <td><strong>{v.name}</strong></td>
                    <td style={{ textAlign: 'center' }}>{v.count}{t.dashboard.count}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: parseFloat(v.avgLt) > 7 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                        {v.avgLt}{t.dashboard.day}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: parseFloat(v.avgDefRate) > 2 ? '#dc2626' : parseFloat(v.avgDefRate) > 1 ? '#f59e0b' : '#16a34a', fontWeight: 600 }}>
                        {v.avgDefRate}%
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmtCurrency(v.avgCost)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`table-badge ${gradeColor}`}>{grade}{lang === 'ko' ? '등급' : '等級'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
